import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import de from "./locales/de/translation.json";
import en from "./locales/en/translation.json";
import es from "./locales/es/translation.json";
import zh from "./locales/zh/translation.json";
import fr from "./locales/fr/translation.json";
import ko from "./locales/ko/translation.json";
import ja from "./locales/ja/translation.json";
import th from "./locales/th/translation.json";
import { api } from "@/lib/api";

export const SUPPORTED_LANGS = [
  { code: "de", short: "DE", native: "Deutsch" },
  { code: "en", short: "EN", native: "English" },
  { code: "es", short: "ES", native: "Español" },
  { code: "zh", short: "中", native: "中文" },
  { code: "fr", short: "FR", native: "Français" },
  { code: "ko", short: "한", native: "한국어" },
  { code: "ja", short: "日", native: "日本語" },
  { code: "th", short: "ไ", native: "ไทย" },
];

const SUPPORTED_CODES = SUPPORTED_LANGS.map((l) => l.code);

export const LOCALE_MAP = {
  de: "de-DE",
  en: "en-US",
  es: "es-ES",
  zh: "zh-CN",
  fr: "fr-FR",
  ko: "ko-KR",
  ja: "ja-JP",
  th: "th-TH",
};

const STORAGE_KEY = "nwp_lang";
const DETECTED_FLAG_KEY = "nwp_lang_detected";

function getManualLang() {
  try {
    const saved = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (saved && SUPPORTED_CODES.includes(saved)) return saved;
  } catch (_) {}
  return null;
}

function getInitialLang() {
  // 1. Manual user choice always wins
  const manual = getManualLang();
  if (manual) return manual;
  // 2. Otherwise default to English while async geo-detect runs in the background
  return "en";
}

i18n
  .use(initReactI18next)
  .init({
    resources: {
      de: { translation: de },
      en: { translation: en },
      es: { translation: es },
      zh: { translation: zh },
      fr: { translation: fr },
      ko: { translation: ko },
      ja: { translation: ja },
      th: { translation: th },
    },
    lng: getInitialLang(),
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

/**
 * Geo-detect the visitor's language on first visit only.
 * - If the user has already picked a language manually (localStorage hit) → skip.
 * - Otherwise call the backend, apply the language, mark as detected (sessionStorage).
 * - Detected language is NOT persisted to localStorage; only manual picks persist.
 */
async function autoDetectLanguage() {
  if (getManualLang()) return; // manual choice → respect it
  try {
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(DETECTED_FLAG_KEY)) {
      // already attempted this session — don't spam
      return;
    }
  } catch (_) {}
  try {
    const { data } = await api.get("/i18n/detect");
    const lang = data?.language;
    if (lang && SUPPORTED_CODES.includes(lang) && lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
    try { sessionStorage.setItem(DETECTED_FLAG_KEY, data?.source || "ok"); } catch (_) {}
  } catch (_) {
    // silent fallback — already on "en"
  }
}

// kick off detection in the background, post-init
autoDetectLanguage();

export function changeLanguage(code) {
  if (!SUPPORTED_CODES.includes(code)) return;
  try { localStorage.setItem(STORAGE_KEY, code); } catch (_) {}
  i18n.changeLanguage(code);
}

export default i18n;
